/**
 * Premi & Attestati — galleria riconoscimenti.
 *
 * Custom element <awards-gallery> che gestisce:
 * - filtro per anno (pill buttons)
 * - animazione di ingresso delle card (IntersectionObserver)
 * - lightbox <dialog> con navigazione prec/succ e tastiera
 */
class AwardsGallery extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-awards-grid]');
    this.items = Array.from(this.querySelectorAll('.premi-attestati__item'));
    this.triggers = Array.from(this.querySelectorAll('[data-award-trigger]'));
    this.filters = Array.from(this.querySelectorAll('[data-filter]'));
    this.emptyMessage = this.querySelector('[data-awards-empty]');

    this.dialog = this.querySelector('[data-awards-lightbox]');
    this.lightboxImage = this.querySelector('[data-lightbox-image]');
    this.lightboxYear = this.querySelector('[data-lightbox-year]');
    this.lightboxTitle = this.querySelector('[data-lightbox-title]');
    this.lightboxSubtitle = this.querySelector('[data-lightbox-subtitle]');
    this.lightboxCounter = this.querySelector('[data-lightbox-counter]');

    this.currentIndex = 0;

    this.#bindFilters();
    this.#bindLightbox();
    this.#observeItems();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /** Card attualmente visibili (rispettando il filtro attivo). */
  get visibleTriggers() {
    return this.triggers.filter((trigger) => !trigger.closest('.premi-attestati__item')?.hidden);
  }

  #bindFilters() {
    for (const button of this.filters) {
      button.addEventListener('click', () => this.#applyFilter(button));
    }
  }

  #applyFilter(activeButton) {
    const year = activeButton.dataset.filter;

    for (const button of this.filters) {
      const isActive = button === activeButton;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    }

    let visibleCount = 0;
    this.items.forEach((item) => {
      const matches = year === 'all' || item.dataset.year === year;
      item.hidden = !matches;
      if (matches) {
        item.style.setProperty('--reveal-delay', `${(visibleCount % 6) * 55}ms`);
        item.classList.remove('is-inview');
        requestAnimationFrame(() => requestAnimationFrame(() => item.classList.add('is-inview')));
        visibleCount += 1;
      }
    });

    if (this.emptyMessage) this.emptyMessage.hidden = visibleCount > 0;
  }

  #observeItems() {
    if (!('IntersectionObserver' in window)) {
      this.items.forEach((item) => item.classList.add('is-inview'));
      return;
    }

    let revealCounter = 0;
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const item = entry.target;
          item.style.setProperty('--reveal-delay', `${(revealCounter % 6) * 55}ms`);
          revealCounter += 1;
          item.classList.add('is-inview');
          this.observer.unobserve(item);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
    );

    this.items.forEach((item) => this.observer.observe(item));
  }

  #bindLightbox() {
    if (!this.dialog) return;

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.#open(trigger));
    });

    this.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.dialog.close());
    this.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.#step(-1));
    this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.#step(1));

    // Chiudi cliccando sullo sfondo (fuori dal contenuto).
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.#step(-1);
      if (event.key === 'ArrowRight') this.#step(1);
    });
  }

  #open(trigger) {
    const visible = this.visibleTriggers;
    this.currentIndex = Math.max(0, visible.indexOf(trigger));
    this.#render(visible);
    if (!this.dialog.open) this.dialog.showModal();
  }

  #step(direction) {
    const visible = this.visibleTriggers;
    if (visible.length === 0) return;
    this.currentIndex = (this.currentIndex + direction + visible.length) % visible.length;
    this.#render(visible);
  }

  #render(visible) {
    const trigger = visible[this.currentIndex];
    if (!trigger) return;

    const { full, title, subtitle, year } = trigger.dataset;

    this.lightboxImage.src = full;
    this.lightboxImage.alt = title ? `Attestato: ${title}` : 'Attestato';
    if (this.lightboxYear) this.lightboxYear.textContent = year || '';
    if (this.lightboxTitle) this.lightboxTitle.textContent = title || '';
    if (this.lightboxSubtitle) {
      this.lightboxSubtitle.textContent = subtitle || '';
      this.lightboxSubtitle.hidden = !subtitle;
    }
    if (this.lightboxCounter) {
      this.lightboxCounter.textContent = visible.length > 1 ? `${this.currentIndex + 1} / ${visible.length}` : '';
    }

    const showNav = visible.length > 1;
    this.querySelectorAll('.awards-lightbox__nav').forEach((button) => {
      button.toggleAttribute('disabled', !showNav);
      button.style.display = showNav ? '' : 'none';
    });
  }
}

if (!customElements.get('awards-gallery')) {
  customElements.define('awards-gallery', AwardsGallery);
}
